import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphDefault_AtlassianSearchPage$key } from './__generated__/searchGraphDefault_AtlassianSearchPage.graphql';
import { messages } from './messages';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

// We accept a missing product for the default renderer
export type SearchGraphDefaultResultProps<T> = Omit<SearchGraphResultProps<T>, 'product'> & {
	product?: ProductKeys3P;
};

export const SearchGraphDefault = ({
	node,
	position,
	cloudId,
	product,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	icon,
	entityType,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphDefaultResultProps<searchGraphDefault_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphDefault_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				title
				url
				subtype
				excerpt
				lastModifiedDate
				permissionLevel
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { id, url, title, subtype, excerpt, lastModifiedDate, permissionLevel } = data;
	const isNotPublic = isNonPublic(permissionLevel);

	const lastModifiedComponent = lastModifiedDate && (
		<LastModified lastModified={lastModifiedDate} />
	);

	const resultType = subtype || entityType;
	const labelMessage = messages[resultType];
	const label = resultType && labelMessage ? formatMessage(labelMessage) : getLabel(resultType);

	const headerMetadataParts = [
		['label', <Box xcss={typeLabelContainer}>{label}</Box>],
		['last-modified', lastModifiedComponent],
	];

	const enrichedTitle = enrichExcerpt(title);
	const description = excerpt && <DescriptionBody description={processExcerpt(excerpt)} />;
	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultContextMenu={onResultContextMenu}
			onResultClicked={onResultClicked}
			isAllProductSelected={!selectedProduct}
		/>
	) : (
		isNotPublic && (
			<Box xcss={footerContainer}>
				<RestrictedLozenge restriction={permissionLevel} />
			</Box>
		)
	);

	return (
		<SearchResult
			{...data}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
